import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Button, Collapse, Form, Layout, Modal, Spin, Typography } from "antd";
import {
  PACKING_COLOR_LIST,
  PACKING_STYLE_LIST,
  PACKING_TYPES,
  PO_MAP_ENDPOINT,
  PO_WISE_PACKING_ADD,
  PO_WISE_PACKING_UPDATE,
} from "apiServices/API_ENDPOINTS";
import { getData, postData } from "apiServices/common";
import { alertPop } from "apiServices/common/helper";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { PACKING_CREATE_PERMISSION } from "routes/permissions";
import { hasPermission, isArrayAndHasValue } from "utils/functions";
import AppPageHeader from "../../../../common/PageHeader";
import InventoryLayOutCommon from "../../../InventoryFabric/LayOut/InventoryLayOutCommon";
import PackingDetails from "../PackingDetails";
import PackingReqForm from "./PackingReqForm";

const getStyleId = (style) => style?.style_id ?? style?.id;

const mergeStyleLists = (apiStyles = [], savedStyles = []) => {
  if (!isArrayAndHasValue(savedStyles)) {
    return apiStyles;
  }
  if (!isArrayAndHasValue(apiStyles)) {
    return savedStyles;
  }

  const mergedMap = new Map();

  apiStyles.forEach((style) => {
    const styleId = getStyleId(style);
    mergedMap.set(styleId, {
      ...style,
      style_id: styleId,
      sub_styles: [...(style?.sub_styles || [])],
    });
  });

  savedStyles.forEach((savedStyle) => {
    const styleId = getStyleId(savedStyle);
    const existing = mergedMap.get(styleId);

    if (!existing) {
      mergedMap.set(styleId, {
        ...savedStyle,
        style_id: styleId,
        sub_styles: [...(savedStyle?.sub_styles || [])],
      });
      return;
    }

    const subStyleIds = new Set((existing.sub_styles || []).map((subStyle) => subStyle.id));
    (savedStyle?.sub_styles || []).forEach((subStyle) => {
      if (!subStyleIds.has(subStyle.id)) {
        existing.sub_styles.push(subStyle);
        subStyleIds.add(subStyle.id);
      }
    });
  });

  return Array.from(mergedMap.values());
};

const CreatePackingReq = (props) => {
  // Props
  const { isEdit, isView, selectedPoEditData, poMapId, editId, formData, editStyleList } = props;

  // States
  const [poList, setPoList] = useState(null);
  const [styleList, setStyleList] = useState(null);
  const [poDetailsData, setPoDetailsData] = useState([{ unique_id: 0 }]);
  const [selectedPoColorList, setSelectedPoColorList] = useState(null);
  const [selectedPOUniqueID, setSelectedPOUniqueID] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFactory, setIsFactory] = useState(false);
  const [packingTypes, setPackingTypes] = useState([]);
  const [useSubStyleFromDetails, setUseSubStyleFromDetails] = useState(false);
  const [styleListLoading, setStyleListLoading] = useState(!!isEdit);

  // Router
  const navigate = useNavigate();

  // Antd
  const { Content } = Layout;
  const { Panel } = Collapse;
  const [PackReqForm] = Form.useForm();
  const { confirm } = Modal;
  const { Title } = Typography;

  const extra = (
    <div className="extra-button-container">
      {isView ? (
        <Button
          size="small"
          onClick={() => {
            navigate(`/merchandising/packing-requirements`);
          }}
        >
          {" "}
          Back to List
        </Button>
      ) : (
        <>
          <Button
            htmlType="submit"
            type="primary"
            size="small"
            style={{ marginRight: 5 }}
            onClick={() => PackReqForm.submit()}
            disabled={!hasPermission([PACKING_CREATE_PERMISSION]) || isSubmitting}
            loading={isSubmitting}
          >
            {hasPermission([PACKING_CREATE_PERMISSION]) ? "Submit" : "No Permission"}
          </Button>
          <Button
            size="small"
            htmlType="submit"
            onClick={() => {
              // reset all field
              PackReqForm.resetFields();
              // navigate.push("/merchandising/packing-requirements");
            }}
          >
            {" "}
            Reset
          </Button>
        </>
      )}
    </div>
  );

  const getPOList = async (filterValues) => {
    let query = `${PO_MAP_ENDPOINT}`;
    const bodyData = {
      per_page: filterValues?.per_page || 5,
      ...filterValues,
    };
    const response = await getData(query, false, bodyData);

    if (response) {
      setPoList(response?.data?.data?.data);
    } else {
      alertPop("error", "Something went wrong");
    }
  };

  const getDataFromPOMapId = useCallback(
    (po_id) => {
      const selectedPO = poList?.find((po) => po?.id === po_id);
      setIsFactory(selectedPO?.sub_contract_factory_id ? 1 : 0);
      selectedPO &&
        PackReqForm.setFieldsValue({
          original_po: selectedPO?.original_po,
          internal_po: selectedPO?.internal_po,
          buying_office_name: selectedPO?.buying_office_name,
          buying_office_id: selectedPO?.buying_office_id,
          buyer_name: selectedPO?.buyer_name,
          buyer_id: selectedPO?.buyer_id,
          sub_contract_factory_id: selectedPO?.sub_contract_factory_id || null,
          sub_contract_factory_name: selectedPO?.sub_contract_factory_name || null,
          sales_contract_id: selectedPO?.sales_contract_id,
          reference_no: selectedPO?.reference_no,
          po_map_id: selectedPO?.id,
          total_order_quantity: selectedPO?.total_order_quantity ? Number(selectedPO?.total_order_quantity) : 0,
          p_o_type_name: selectedPO?.p_o_type_name,
          packing_types: isArrayAndHasValue(selectedPO?.packing_type_map)
            ? selectedPO?.packing_type_map?.map((item) => item?.packing_type_id)
            : [],
          _packing_types: isArrayAndHasValue(selectedPO?.packing_type_map)
            ? selectedPO?.packing_type_map?.map((item) => ({
                packing_type_id: item?.packing_type_id,
                is_delete: 0,
                id: item?.id || 0,
              }))
            : [],
          department_name: selectedPO?.department_name,
        });
    },
    [PackReqForm, poList],
  );

  useEffect(() => {
    // Get PO List on first render

    if (isEdit) {
      getPOList({ po_map_id: poMapId });
    } else {
      getPOList();
    }
    getPackingTypes();
  }, [isEdit, poMapId]);

  useEffect(() => {
    // When in edit or view mode, set podetailsdata for table
    if (isArrayAndHasValue(selectedPoEditData)) {
      setPoDetailsData(selectedPoEditData);

      // Set value of PO Packing Req Qty field value in form
      const totalPackReqQty = selectedPoEditData?.reduce((result, item) => {
        return result + Number(item?.carton_pack_req_qty);
      }, 0);
      PackReqForm.setFieldsValue({
        total_packing_requirements: totalPackReqQty || 0,
      });
    }
  }, [isEdit, isView, selectedPoEditData, PackReqForm]);

  useEffect(() => {
    if (poMapId || poMapId === 0) {
      getDataFromPOMapId(poMapId);
    }
  }, [poMapId, getDataFromPOMapId]);

  useEffect(() => {
    // Populate form with data from API response when in edit/view mode
    if (isEdit && formData) {
      PackReqForm.setFieldsValue(formData);
      setIsFactory(formData?.sub_contract_factory_id ? 1 : 0);
    }
  }, [isEdit, formData, PackReqForm]);

  useEffect(() => {
    if (poMapId == null && poMapId !== 0) {
      return;
    }
    if (!isArrayAndHasValue(poList)) {
      return;
    }

    const selectedPO = poList.find((po) => po.id === poMapId);
    if (!selectedPO) {
      return;
    }

    // In edit mode wait for saved styles so we can merge once with the API list
    if (isEdit && editStyleList === null) {
      return;
    }

    getStyleList(selectedPO, isEdit ? editStyleList : null);
  }, [poMapId, poList, isEdit, editStyleList]);

  const isWaitingForStyleDependencies =
    isEdit && poMapId != null && (!isArrayAndHasValue(poList) || editStyleList === null);
  const showStylePoDetailsLoader = styleListLoading || isWaitingForStyleDependencies;

  const cartonQtyCalculation = (cartonData) => {
    // Flatten the structure into a single array of objects
    let flatData = [];

    isArrayAndHasValue(cartonData) &&
      cartonData?.forEach((style) => {
        style?.colors?.forEach((color) => {
          color?.sizes?.forEach((size) => {
            flatData.push({
              style_id: style.style_id,
              sub_style_id: color.sub_style_id,
              color_id: color.color_id,
              size_id: size.size_id,
              distributed_qty: size.quantity,
            });
          });
        });
      });

    // Aggregate the quantities
    const result = Object.values(
      flatData.reduce((acc, { style_id, color_id, size_id, distributed_qty, sub_style_id }) => {
        const key = `${style_id}-${color_id}-${size_id}`;
        if (!acc[key]) {
          acc[key] = {
            style_id,
            color_id,
            size_id,
            distributed_qty,
            sub_style_id,
          };
        } else {
          acc[key].distributed_qty += distributed_qty;
        }
        return acc;
      }, {}),
    );

    return result;
  };

  const getPackingTypeList = (values) => {
    if (!isArrayAndHasValue(values?._packing_types) && isArrayAndHasValue(values?.packing_types)) {
      const packing_types = values?.packing_types?.map((item) => ({
        packing_type_id: item,
        is_delete: 0,
        id: 0,
      }));
      return packing_types;
    }
    return values?._packing_types;
  };

  const onFinishPacking = async (values) => {
    // setIsSubmitting(true);

    let bodyData;

    // packing_types in required shape [{ packing_type_id, is_delete, id }]
    const packing_types = getPackingTypeList(values);

    // Common builder for packing_requirement_details for both create and update
    const packing_requirement_details =
      poDetailsData?.map((item) => {
        const carton_from = Number(item?.start_range) || 0;
        const carton_to = Number(item?.end_range) || 0;
        const total_carton_qty =
          carton_from && carton_to && carton_to >= carton_from
            ? carton_to - carton_from + 1
            : Number(item?.carton_qty) || 0;

        const total_quantity = Number(item?.total_quantity) || 0;
        const per_carton_garment_qty = total_quantity;
        const total_garment_qty = total_carton_qty * per_carton_garment_qty;

        const length = Number(item?.length) || 0;
        const width = Number(item?.width) || 0;
        const height = Number(item?.height) || 0;
        const cbm = Number(item?.cbm) || 0;
        const net = Number(item?.net) || 0;
        const gross = Number(item?.gross) || 0;

        // Flatten colors -> packing_size_ratios
        const packing_size_ratios = [];
        if (isArrayAndHasValue(item?.colors)) {
          item.colors.forEach((color) => {
            if (isArrayAndHasValue(color?.sizes)) {
              color.sizes.forEach((size) => {
                packing_size_ratios.push({
                  id: isEdit ? size?.id || 0 : 0,
                  style_id: color?.style_id || null,
                  sub_style_id: color?.sub_style_id || null,
                  size_id: size?.size_id || null,
                  color_id: color?.color_id || null,
                  shade: color?.shade || null,
                  pack: color?.pack || null,
                  qty: Number(size?.quantity) || 0,
                  is_delete: 0,
                });
              });
            }
          });
        }

        return {
          id: isEdit ? item?.id || 0 : 0,
          packing_type_id: item?.packing_type_id || null,
          carton_from,
          carton_to,
          assortment: item?.assortment || null,
          customer_po: item?.customer_po || null,
          item: item?.item || null,
          upc: item?.upc || null,
          total_carton_qty,
          initial_length: length,
          initial_height: height,
          initial_width: width,
          initial_cbm: cbm,
          initial_net_weight: net,
          initial_gross_weight: gross,
          commercial_length: length || null,
          commercial_height: height || null,
          commercial_width: width || null,
          commercial_cbm: cbm || null,
          commercial_net_weight: net || null,
          commercial_gross_weight: gross || null,
          total_garment_qty,
          per_carton_garment_qty,
          is_delete: 0,
          packing_size_ratios,
        };
      }) || [];

    bodyData = {
      buying_office_id: values?.buying_office_id || null,
      buyer_id: values?.buyer_id || null,
      sub_contract_factory_id: values?.sub_contract_factory_id || null,
      sales_contract_id: values?.sales_contract_id || null,
      po_map_id: values?.po_map_id || null,
      container_number: null,
      seal_number: null,
      fedder_vessel: null,
      mother_vessel: null,
      eta: null,
      etd: null,
      subcontract_status: isFactory ? 1 : 0,
      status: isEdit ? values?.status || "Created" : "Created",
      packing_requirement_details,
      packing_types,
    };

    const query = isEdit ? `${PO_WISE_PACKING_UPDATE}/${editId}` : `${PO_WISE_PACKING_ADD}`;

    const response = await postData(query, bodyData);

    if (response && response?.code === 200) {
      alertPop("success", `Packing Requirement ${isEdit ? "Updated" : "Inserted"} Successfully`);
      setIsSubmitting(false);
      navigate(`/merchandising/packing-requirements`);
    } else {
      alertPop("error", "Something went wrong");
      setIsSubmitting(false);
    }
  };

  const getStyleList = async (poData, savedStyles = null) => {
    setStyleListLoading(true);

    try {
      const query = `${PACKING_STYLE_LIST}`;
      const bodyData = {
        po_map_id: poData?.id,
        sales_contract_id: poData?.sales_contract_id,
        use_both_style_po: poData?.use_both_style_po,
      };

      const response = await getData(query, false, bodyData);

      if (response && response?.data?.code === 200) {
        const apiStyles = response?.data?.data || [];
        const styles = isEdit ? mergeStyleLists(apiStyles, savedStyles) : apiStyles;
        setStyleList(styles);

        // If any style has sub_styles, then sub style will be selected from Style PO Details
        const hasSubStyles = isArrayAndHasValue(styles) && styles.some((style) => isArrayAndHasValue(style?.sub_styles));
        setUseSubStyleFromDetails(hasSubStyles);
      } else if (isEdit && isArrayAndHasValue(savedStyles)) {
        setStyleList(savedStyles);
        const hasSubStyles =
          isArrayAndHasValue(savedStyles) && savedStyles.some((style) => isArrayAndHasValue(style?.sub_styles));
        setUseSubStyleFromDetails(hasSubStyles);
      } else {
        setStyleList([]);
        alertPop("error", `${response?.data?.message?.[0] || "Something went wrong"}`);
      }
    } finally {
      setStyleListLoading(false);
    }
  };

  const getPackingTypes = async (filterValues) => {
    let query = `${PACKING_TYPES}`;
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };
    const response = await getData(query, false, bodyData);
    if (response) {
      setPackingTypes(response?.data?.data || []);
    } else {
      alertPop("error", "Something went wrong");
    }
  };

  const onChangeInternalPO = (value) => {
    const selectedPO = poList?.find((po) => po?.internal_po === value);
    selectedPO &&
      PackReqForm.setFieldsValue({
        original_po: selectedPO?.original_po,
        buying_office_name: selectedPO?.buying_office_name,
        buying_office_id: selectedPO?.buying_office_id,
        buyer_name: selectedPO?.buyer_name,
        buyer_id: selectedPO?.buyer_id,
        sales_contract_id: selectedPO?.sales_contract_id,
        reference_no: selectedPO?.reference_no,
        po_map_id: selectedPO?.id,
        total_order_quantity: selectedPO?.total_order_quantity,
        sub_contract_factory_id: selectedPO?.sub_contract_factory_id || null,
        packing_types: isArrayAndHasValue(selectedPO?.packing_type_map)
          ? selectedPO?.packing_type_map?.map((item) => item?.packing_type_id)
          : [],
        _packing_types: isArrayAndHasValue(selectedPO?.packing_type_map)
          ? selectedPO?.packing_type_map?.map((item) => ({
              packing_type_id: item?.packing_type_id,
              is_delete: 0,
              id: item?.id || 0,
            }))
          : [],
        sub_contract_factory_name: selectedPO?.sub_contract_factory_name || null,
      });
    getStyleList(selectedPO);
    setIsFactory(selectedPO?.sub_contract_factory_id ? 1 : 0);
  };

  const onChangeOriginalPO = (value) => {
    const selectedPO = poList?.find((po) => po?.original_po === value);
    selectedPO &&
      PackReqForm.setFieldsValue({
        internal_po: selectedPO?.internal_po,
        buying_office_name: selectedPO?.buying_office_name,
        buying_office_id: selectedPO?.buying_office_id,
        buyer_name: selectedPO?.buyer_name,
        buyer_id: selectedPO?.buyer_id,
        sales_contract_id: selectedPO?.sales_contract_id,
        reference_no: selectedPO?.reference_no,
        po_map_id: selectedPO?.id,
        total_order_quantity: selectedPO?.total_order_quantity,
        sub_contract_factory_id: selectedPO?.sub_contract_factory_id || null,
        sub_contract_factory_name: selectedPO?.sub_contract_factory_name || null,
        p_o_type_name: selectedPO?.p_o_type_name,
        packing_types: isArrayAndHasValue(selectedPO?.packing_type_map)
          ? selectedPO?.packing_type_map?.map((item) => item?.packing_type_id)
          : [],
        _packing_types: isArrayAndHasValue(selectedPO?.packing_type_map)
          ? selectedPO?.packing_type_map?.map((item) => ({
              packing_type_id: item?.packing_type_id,
              is_delete: 0,
              id: item?.id || 0,
            }))
          : [],
        department_name: selectedPO?.department_name,
      });
    getStyleList(selectedPO);
    setIsFactory(selectedPO?.sub_contract_factory_id ? 1 : 0);
  };

  const copyRow = (row_id) => {
    const foundPoItem = poDetailsData?.find((item) => item?.unique_id === row_id);
    const clonedFoundPoItem = structuredClone(foundPoItem);
    const unique_id = Date.now();

    const new_start_range = clonedFoundPoItem?.end_range + 1;
    const new_end_range = new_start_range + (clonedFoundPoItem?.carton_qty - 1);

    // Set id to 0 for the copied row to mark it as new
    clonedFoundPoItem.id = 0;

    // Set id to 0 for all nested color/size data (these become packing_size_ratios)
    if (isArrayAndHasValue(clonedFoundPoItem?.colors)) {
      clonedFoundPoItem.colors.forEach((color) => {
        if (isArrayAndHasValue(color?.sizes)) {
          color.sizes.forEach((size) => {
            size.id = 0;
          });
        }
      });
    }

    clonedFoundPoItem?.sizes?.forEach((sizeItem, index) => {
      sizeItem.po_detail_id = 0;
    });

    const updatePoDetails = [
      ...poDetailsData,
      {
        ...clonedFoundPoItem,
        unique_id: unique_id,
        start_range: new_start_range,
        end_range: new_end_range,
      },
    ];
    setPoDetailsData(updatePoDetails);
  };

  const removeRow = (row_index) => {
    confirm({
      title: "Do you want to delete this row?",
      icon: <ExclamationCircleOutlined />,
      onOk() {
        const foundPoItem = poDetailsData?.find((item) => item?.unique_id === row_index);
        const updatedPoDetailsData = poDetailsData.filter((item) => item?.unique_id !== row_index);
        setPoDetailsData(updatedPoDetailsData);
      },
    });
  };

  const onChangePoDetailsValue = (value, index, keyName) => {
    // Copy poDetailsData state array
    const poDetailsDataCopy = [...poDetailsData];
    // If keyName is start_range or end_range, then calculate carton_qty

    const foundPoItem = poDetailsDataCopy?.find((item) => item?.unique_id === index);
    foundPoItem[keyName] = value;
    if (["start_range", "end_range"].includes(keyName) && poDetailsDataCopy?.length > 1) {
      foundPoItem["carton_pack_req_qty"] = Math.abs(foundPoItem?.carton_qty) * foundPoItem?.total_quantity;
    }
    setPoDetailsData(poDetailsDataCopy);
  };

  const calculateQty = (uniqueId) => {
    const foundPoItem = poDetailsData?.find((item) => item?.unique_id === uniqueId);
    const qty = foundPoItem?.end_range - foundPoItem?.start_range;
    if (foundPoItem?.end_range && foundPoItem?.start_range) {
      foundPoItem["carton_qty"] = foundPoItem?.end_range - foundPoItem?.start_range + 1;
    } else if (qty && qty > 0) {
      foundPoItem["carton_qty"] = qty + 1;
    } else {
      foundPoItem["carton_qty"] = 0;
    }
  };

  const calculateCBM = (uniqueId) => {
    const foundPoItem = poDetailsData?.find((item) => item?.unique_id === uniqueId);
    const cbm = (foundPoItem?.length * foundPoItem?.width * foundPoItem?.height) / 1000000;
    const totalCbm = cbm * foundPoItem?.carton_qty;

    if (cbm && cbm > 0) {
      foundPoItem["cbm"] = cbm;
    } else {
      foundPoItem["cbm"] = 0;
    }
    if (totalCbm && totalCbm > 0) {
      foundPoItem["total_cbm"] = totalCbm;
    } else {
      foundPoItem["total_cbm"] = 0;
    }
  };

  const calculateTotalGrossWeight = (uniqueId) => {
    const poDetailsDataCopy = [...poDetailsData];
    const foundPoItem = poDetailsDataCopy?.find((item) => item?.unique_id === uniqueId);
    const totalGrossWeight = foundPoItem?.gross * foundPoItem?.carton_qty;

    if (totalGrossWeight && totalGrossWeight > 0) {
      foundPoItem["total_gross"] = totalGrossWeight;
    } else {
      foundPoItem["total_gross"] = 0;
    }

    setPoDetailsData(poDetailsDataCopy);
  };

  const calculateTotalNetWeight = (uniqueId) => {
    const poDetailsDataCopy = [...poDetailsData];
    const foundPoItem = poDetailsDataCopy?.find((item) => item?.unique_id === uniqueId);
    const totalNetWeight = foundPoItem?.net * foundPoItem?.carton_qty;

    if (totalNetWeight && totalNetWeight > 0) {
      foundPoItem["total_net"] = totalNetWeight;
    } else {
      foundPoItem["total_net"] = 0;
    }

    setPoDetailsData(poDetailsDataCopy);
  };

  const fetchStyleColors = useCallback(
    async (styleId, options = {}) => {
      if (!styleId) return null;

      const internal_po = PackReqForm.getFieldValue("internal_po");
      const poData = poList?.find((po) => po?.internal_po === internal_po);
      const response = await getData(PACKING_COLOR_LIST, false, {
        style_id: styleId,
        po_map_id: poData?.id,
        sales_contract_id: poData?.sales_contract_id,
        use_both_style_po: poData?.use_both_style_po,
      });

      if (response?.data?.code === 200) {
        return response.data.data;
      }

      if (!options.silent) {
        alertPop("error", `${response?.data?.message?.[0] || "Something went wrong"}`);
      }
      return null;
    },
    [PackReqForm, poList],
  );

  const hasSelectedValue = (value) => value !== 0 && value !== "" && value !== null && value !== undefined;

  const checkColorOrSubStyleIsRequired = (list) => {
    const hasRowStyle = useSubStyleFromDetails
      ? list?.every((item) => hasSelectedValue(item?.style_id) && hasSelectedValue(item?.sub_style_id))
      : list?.every((item) => hasSelectedValue(item?.style_id));
    const hasColor = list?.every((item) => hasSelectedValue(item?.color_id));

    if (!hasRowStyle || !hasColor) return false;

    if (!useSubStyleFromDetails) {
      const needsSubStyle = list?.some((item) => isArrayAndHasValue(item?.color_options?.sub_styles));
      if (needsSubStyle) {
        return list?.every((item) => hasSelectedValue(item?.sub_style_id));
      }
    }

    return true;
  };

  const insertSizeTableData = (tableData, sizeTrackForm, cb) => {
    const isColorAndSubStyleRequired = checkColorOrSubStyleIsRequired(tableData);
    if (!isColorAndSubStyleRequired) {
      const errorMessage = useSubStyleFromDetails
        ? "Please select sub style and color"
        : "Please select style and color";
      alertPop("error", errorMessage, "error", "bottomRight");
      return;
    }
    const poDetailsDataCopy = [...poDetailsData];
    const foundPoItem = poDetailsDataCopy?.find((item) => item?.unique_id === selectedPOUniqueID);
    const sizeRatioTotal = sizeTrackForm.getFieldValue("size_ratio_total")
      ? Number(sizeTrackForm.getFieldValue("size_ratio_total"))
      : 0;
    const cartonPackReqQtyTotal = tableData?.reduce((result, item) => {
      return result + Number(item?.req_color_total);
    }, 0);

    const carton_from = sizeTrackForm.getFieldValue("carton_from");
    const carton_to = sizeTrackForm.getFieldValue("carton_to");

    if (isArrayAndHasValue(tableData)) {
      // Removing unique_id from tableData
      const updatedData = tableData.map((item) => {
        return item;
      });
      foundPoItem["colors"] = updatedData;
      foundPoItem["total_quantity"] = sizeRatioTotal;
      foundPoItem["carton_pack_req_qty"] = cartonPackReqQtyTotal;

      // Update From/To and dependent carton quantity
      if (carton_from && carton_to && carton_to >= carton_from) {
        foundPoItem["start_range"] = Number(carton_from);
        foundPoItem["end_range"] = Number(carton_to);
        foundPoItem["carton_qty"] = Number(carton_to) - Number(carton_from) + 1;
      }
    }

    // Calculate and set total packing req qty
    // Set value to PackReqForm form

    const total_carton_pack_req_qty = poDetailsDataCopy?.reduce((result, item) => {
      return result + Number(item?.carton_qty) * Number(item?.total_quantity);
    }, 0);
    PackReqForm.setFieldsValue({
      total_packing_requirements: total_carton_pack_req_qty || 0,
    });

    alertPop("success", "Data Inserted");

    setPoDetailsData(poDetailsDataCopy);
    if (cb && typeof cb === "function") {
      cb();
    }
  };

  return (
    <>
      <InventoryLayOutCommon>
        <AppPageHeader title={<Title level={5}>Packing Requirements Entry</Title>} extra={extra} />
        <Content className="item-details">
          <Collapse defaultActiveKey={["1"]} style={{ marginTop: 10 }}>
            <Panel header={<b>Requirements Details</b>} key="1">
              <Form layout="vertical" onFinish={onFinishPacking} form={PackReqForm} disabled={isView}>
                <PackingReqForm
                  poList={poList}
                  getPOList={getPOList}
                  onChangeInternalPO={onChangeInternalPO}
                  onChangeOriginalPO={onChangeOriginalPO}
                  isFactory={isFactory}
                  isView={isView}
                  packingTypes={packingTypes}
                  getPackingTypes={getPackingTypes}
                  form={PackReqForm}
                />
              </Form>
            </Panel>
          </Collapse>
          <PackingDetails
            styleList={styleList}
            styleListLoading={showStylePoDetailsLoader}
            poDetailsData={poDetailsData}
            copyRow={copyRow}
            removeRow={removeRow}
            onChangePoDetailsValue={onChangePoDetailsValue}
            calculateQty={calculateQty}
            calculateCBM={calculateCBM}
            calculateTotalGrossWeight={calculateTotalGrossWeight}
            calculateTotalNetWeight={calculateTotalNetWeight}
            selectedPoColorList={selectedPoColorList}
            fetchStyleColors={fetchStyleColors}
            poList={poList}
            setSelectedPoColorList={setSelectedPoColorList}
            PackReqForm={PackReqForm}
            selectedPOUniqueID={selectedPOUniqueID}
            setSelectedPOUniqueID={setSelectedPOUniqueID}
            insertSizeTableData={insertSizeTableData}
            isEdit={isEdit}
            setPoDetailsData={setPoDetailsData}
            isView={isView}
            useSubStyleFromDetails={useSubStyleFromDetails}
            packingTypes={packingTypes}
          />
        </Content>
      </InventoryLayOutCommon>
    </>
  );
};

export default CreatePackingReq;
